import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';

import {
  getAdminPermissions,
  lookupAdminRefundUsers,
  previewAdminPersonalRefund,
  processAdminPersonalRefund,
  resendAdminPaymentDocuments,
} from '../../api/adminService';
import { useToast } from '../../components/Toast';
import { auth } from '../../firebase/config';
import './RefundConsolePage.css';

const MATCH_LABELS = {
  email: '이메일 일치',
  account_name: '계정명 일치',
  uid: 'UID 일치',
  order_no: '주문번호 일치',
  payment_key: '결제키 일치',
};

const DOCUMENT_STATUSES = new Set(['completed', 'done', 'paid', 'success', 'partial_canceled']);
const NON_PAYMENT_METHODS = new Set(['coupon', 'referral', 'invite', 'free', 'trial', 'admin']);

export const canResendPaymentDocuments = (payment) => {
  const status = String(payment?.status || 'completed').toLowerCase();
  if (!DOCUMENT_STATUSES.has(status) || payment?.refund_reconciliation_required) return false;
  if (NON_PAYMENT_METHODS.has(String(payment?.payment_method || '').toLowerCase())) return false;
  const amount = status === 'partial_canceled'
    ? Number(payment?.retained_amount || 0)
    : Number(payment?.amount || 0);
  return amount > 0 && Boolean(payment?.payment_key);
};

const STATUS_LABELS = {
  completed: '결제 완료',
  done: '결제 완료',
  paid: '결제 완료',
  refund_pending: '환불 처리 중',
  refunded: '환불 완료',
  partial_canceled: '일할 환불 완료',
  failed: '결제 실패',
};

const formatCurrency = (value) => `${Number(value || 0).toLocaleString('ko-KR')}원`;

const getTodayInKst = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const formatDate = (value, withTime = false) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('ko-KR', withTime
    ? { dateStyle: 'medium', timeStyle: 'short' }
    : { dateStyle: 'medium' });
};

const getSubscriptionLabel = (subscription = {}) => {
  const type = String(subscription.type || 'free');
  const status = String(subscription.status || 'active');
  if (!type.startsWith('pro') || status !== 'active') return '무료 이용 중';
  return `PRO 이용 중 · ${formatDate(subscription.end_date)}까지`;
};

const RefundConsoleSkeleton = () => (
  <div className="admin-refund-skeleton" aria-label="검색 결과를 불러오는 중">
    <div className="admin-refund-skeleton-line admin-refund-skeleton-line-wide" />
    <div className="admin-refund-skeleton-card" />
    <div className="admin-refund-skeleton-card" />
  </div>
);

const AccessPanel = ({ title, description, onBack, actionLabel = '관리자 홈으로' }) => (
  <main className="admin-refund-access-panel">
    <h1>{title}</h1>
    <p>{description}</p>
    <button type="button" className="admin-refund-button-secondary" onClick={onBack}>
      {actionLabel}
    </button>
  </main>
);

const RefundConsolePage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [user, authLoading] = useAuthState(auth);
  const currentUserId = user?.uid;
  const [permissions, setPermissions] = useState(null);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [permissionError, setPermissionError] = useState('');
  const [permissionRetryKey, setPermissionRetryKey] = useState(0);
  const [query, setQuery] = useState('');
  const [resendingDocumentsKey, setResendingDocumentsKey] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);

  const [modal, setModal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [refundReason, setRefundReason] = useState('고객 요청');
  const [refundMode, setRefundMode] = useState('full');
  const [prorationCutoffDate, setProrationCutoffDate] = useState(
    getTodayInKst,
  );
  const [sendNotification, setSendNotification] = useState(true);
  const [forceOutsideWindow, setForceOutsideWindow] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState('');
  const previewRequestIdRef = useRef(0);
  const searchRequestIdRef = useRef(0);
  const modalRef = useRef(null);
  const modalTriggerRef = useRef(null);

  const selectedUser = useMemo(
    () => results.find((item) => item.uid === selectedUid) || results[0] || null,
    [results, selectedUid],
  );

  useEffect(() => {
    let cancelled = false;
    const loadPermissions = async () => {
      if (authLoading) return;
      if (!currentUserId) {
        setPermissionLoading(false);
        return;
      }
      setPermissionLoading(true);
      setPermissionError('');
      try {
        const response = await getAdminPermissions();
        if (!cancelled) setPermissions(response);
      } catch (error) {
        if (!cancelled) {
          setPermissions(null);
          setPermissionError(error.message || '권한 정보를 불러오지 못했어요.');
        }
      } finally {
        if (!cancelled) setPermissionLoading(false);
      }
    };
    loadPermissions();
    return () => {
      cancelled = true;
    };
  }, [authLoading, currentUserId, permissionRetryKey]);

  useEffect(() => {
    if (!modal) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !processing) {
        previewRequestIdRef.current += 1;
        setModal(null);
        window.requestAnimationFrame(() => {
          modalTriggerRef.current?.focus?.();
        });
        return;
      }
      if (event.key === 'Tab' && modalRef.current) {
        const focusable = Array.from(
          modalRef.current.querySelectorAll(
            'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), a[href]',
          ),
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!modalRef.current.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
          return;
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [modal, processing]);

  const resendDocuments = async (account, payment) => {
    if (resendingDocumentsKey) return;
    setResendingDocumentsKey(payment.payment_key);
    try {
      const result = await resendAdminPaymentDocuments({
        userId: account.uid,
        paymentKey: payment.payment_key,
      });
      showToast(`${result.to_email}(으)로 증빙 서류를 다시 보냈어요.`, 'success', 6000);
    } catch (resendError) {
      showToast(resendError?.message || '증빙 서류를 보내지 못했어요.', 'error');
    } finally {
      setResendingDocumentsKey('');
    }
  };

  const searchUsers = async (searchQuery = query) => {
    const normalized = searchQuery.trim();
    if (normalized.length < 2) {
      setSearchError('두 글자 이상 입력해주세요.');
      return;
    }
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    setSearching(true);
    setSearchError('');
    setHasSearched(true);
    try {
      const response = await lookupAdminRefundUsers(normalized);
      if (searchRequestIdRef.current !== requestId) return;
      const nextResults = response.results || [];
      setResults(nextResults);
      setSelectedUid(nextResults[0]?.uid || null);
    } catch (error) {
      if (searchRequestIdRef.current !== requestId) return;
      setResults([]);
      setSelectedUid(null);
      setSearchError(error.message || '사용자를 찾지 못했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      if (searchRequestIdRef.current === requestId) {
        setSearching(false);
      }
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    searchUsers();
  };

  const loadRefundPreview = async ({
    account,
    payment,
    mode,
    cutoffDate,
  }) => {
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    setPreview(null);
    setPreviewError('');
    setProcessError('');
    setConfirmed(false);
    setPreviewLoading(true);
    try {
      const response = await previewAdminPersonalRefund(
        account.uid,
        payment.payment_key,
        {
          refundMode: mode,
          prorationCutoffDate: mode === 'prorated' ? cutoffDate : null,
        },
      );
      if (previewRequestIdRef.current === requestId) {
        setPreview(response.preview || null);
      }
    } catch (error) {
      if (previewRequestIdRef.current === requestId) {
        setPreviewError(error.message || '환불 전 정보를 확인하지 못했어요.');
      }
    } finally {
      if (previewRequestIdRef.current === requestId) {
        setPreviewLoading(false);
      }
    }
  };

  const openRefundModal = async (account, payment) => {
    const defaultCutoffDate = getTodayInKst();
    modalTriggerRef.current = document.activeElement;
    setModal({ account, payment });
    setRefundReason('고객 요청');
    setRefundMode('full');
    setProrationCutoffDate(defaultCutoffDate);
    setSendNotification(true);
    setForceOutsideWindow(false);
    await loadRefundPreview({
      account,
      payment,
      mode: 'full',
      cutoffDate: defaultCutoffDate,
    });
  };

  const handleRefundModeChange = (mode) => {
    if (!modal || processing || mode === refundMode) return;
    setRefundMode(mode);
    setForceOutsideWindow(false);
    loadRefundPreview({
      account: modal.account,
      payment: modal.payment,
      mode,
      cutoffDate: prorationCutoffDate,
    });
  };

  const handleProrationCutoffChange = (value) => {
    setProrationCutoffDate(value);
    setConfirmed(false);
    setProcessError('');
    if (!modal || refundMode !== 'prorated' || !value) return;
    loadRefundPreview({
      account: modal.account,
      payment: modal.payment,
      mode: 'prorated',
      cutoffDate: value,
    });
  };

  const closeModal = () => {
    if (!processing) {
      previewRequestIdRef.current += 1;
      setModal(null);
      window.requestAnimationFrame(() => {
        modalTriggerRef.current?.focus?.();
      });
    }
  };

  const processRefund = async () => {
    if (!modal || !preview) return;
    if (!refundReason.trim()) {
      setProcessError('환불 사유를 입력해주세요.');
      return;
    }
    if (!confirmed) {
      setProcessError('환불 금액과 이용 기간 변경을 확인해주세요.');
      return;
    }
    if (refundMode === 'prorated' && !prorationCutoffDate) {
      setProcessError('환불 기준일을 선택해주세요.');
      return;
    }
    if (!preview.is_within_refund_window && !forceOutsideWindow) {
      setProcessError('7일이 지난 결제예요. 강제 환불 확인을 선택해주세요.');
      return;
    }

    setProcessing(true);
    setProcessError('');
    try {
      const response = await processAdminPersonalRefund({
        userId: modal.account.uid,
        paymentKey: modal.payment.payment_key,
        refundReason,
        forceOutsideWindow,
        sendNotification,
        refundMode,
        prorationCutoffDate: refundMode === 'prorated'
          ? prorationCutoffDate
          : null,
        confirmationQuoteFingerprint: preview.quote_fingerprint || null,
      });
      showToast(response.message || '환불했어요.', 'success', 5000);
      setModal(null);
      window.requestAnimationFrame(() => {
        modalTriggerRef.current?.focus?.();
      });
      await searchUsers(query);
    } catch (error) {
      if (error.code === 'REFUND_PREVIEW_STALE') {
        await loadRefundPreview({
          account: modal.account,
          payment: modal.payment,
          mode: refundMode,
          cutoffDate: prorationCutoffDate,
        });
        setProcessError('금액이나 이용 기간이 달라져 다시 계산했어요. 변경된 내용을 확인해주세요.');
        window.requestAnimationFrame(() => {
          const confirmation = modalRef.current?.querySelector(
            '.admin-refund-checkbox-confirm input',
          );
          const firstFocusable = modalRef.current?.querySelector(
            'button:not(:disabled), input:not(:disabled), textarea:not(:disabled)',
          );
          (confirmation || firstFocusable)?.focus?.();
        });
      } else {
        setProcessError(error.message || '환불하지 못했어요. 결제 상태를 다시 확인해주세요.');
      }
    } finally {
      setProcessing(false);
    }
  };

  if (authLoading || permissionLoading) {
    return (
      <div className="admin-refund-page">
        <RefundConsoleSkeleton />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-refund-page">
        <AccessPanel
          title="로그인이 필요해요"
          description="슈퍼 관리자 계정으로 로그인해주세요."
          onBack={() => navigate('/auth?redirect=/admin/refunds')}
        />
      </div>
    );
  }

  if (permissionError) {
    return (
      <div className="admin-refund-page">
        <AccessPanel
          title="권한을 확인하지 못했어요"
          description={`${permissionError} 잠시 후 다시 확인해주세요.`}
          actionLabel="다시 확인하기"
          onBack={() => setPermissionRetryKey((current) => current + 1)}
        />
      </div>
    );
  }

  if (!permissions?.is_super_admin) {
    return (
      <div className="admin-refund-page">
        <AccessPanel
          title="슈퍼 관리자만 사용할 수 있어요"
          description="결제 취소 권한이 있는 계정으로 다시 확인해주세요."
          onBack={() => navigate('/admin')}
        />
      </div>
    );
  }

  return (
    <div className="admin-refund-page">
      <Helmet>
        <title>개인 결제 환불 | 왓퀴즈 Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="admin-refund-container">
        <header className="admin-refund-header">
          <button
            type="button"
            className="admin-refund-back-button"
            onClick={() => navigate('/admin')}
          >
            관리자 홈
          </button>
          <p className="admin-refund-eyebrow">WhatQuiz Admin</p>
          <h1>개인 결제 환불</h1>
          <p>사용자를 찾고, 환불할 결제 한 건을 선택하세요.</p>
        </header>

        <section className="admin-refund-search-section" aria-labelledby="admin-refund-search-title">
          <h2 id="admin-refund-search-title">사용자 찾기</h2>
          <form className="admin-refund-search-form" onSubmit={handleSearch}>
            <label htmlFor="admin-refund-search-input" className="admin-refund-visually-hidden">
              사용자 검색
            </label>
            <input
              id="admin-refund-search-input"
              value={query}
              onChange={(event) => {
                searchRequestIdRef.current += 1;
                setQuery(event.target.value);
                setSearching(false);
                setHasSearched(false);
                setResults([]);
                setSelectedUid(null);
                setSearchError('');
              }}
              placeholder="이메일, 계정명, UID, 주문번호, 결제키"
              autoComplete="off"
            />
            <button type="submit" disabled={searching || query.trim().length < 2}>
              {searching ? '찾는 중' : '사용자 찾기'}
            </button>
          </form>
          <p className="admin-refund-search-hint">계정명은 정확히 입력해주세요.</p>
          {searchError && (
            <div className="admin-refund-inline-error" role="alert">
              <span>{searchError}</span>
              <button type="button" onClick={() => searchUsers()}>다시 찾기</button>
            </div>
          )}
        </section>

        {searching && <RefundConsoleSkeleton />}

        {!searching && hasSearched && results.length === 0 && !searchError && (
          <section className="admin-refund-empty-state">
            <h2>일치하는 사용자가 없어요</h2>
            <p>이메일이나 주문번호를 다시 확인해주세요.</p>
          </section>
        )}

        {!searching && results.length > 0 && (
          <>
            {results.length > 1 && (
              <section className="admin-refund-candidate-section" aria-labelledby="admin-refund-candidates-title">
                <div className="admin-refund-section-heading">
                  <h2 id="admin-refund-candidates-title">동명이인 {results.length}명</h2>
                  <p>이메일을 보고 환불할 계정을 선택하세요.</p>
                </div>
                <div className="admin-refund-candidate-list">
                  {results.map((account) => (
                    <button
                      type="button"
                      key={account.uid}
                      className={`admin-refund-candidate ${selectedUser?.uid === account.uid ? 'is-selected' : ''}`}
                      onClick={() => setSelectedUid(account.uid)}
                      aria-pressed={selectedUser?.uid === account.uid}
                    >
                      <strong>{account.display_name || '이름 없음'}</strong>
                      <span>{account.email || account.uid}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {selectedUser && (
              <section className="admin-refund-result-section" aria-labelledby="admin-refund-account-title">
                <div className="admin-refund-account-card">
                  <div>
                    <div className="admin-refund-match-row">
                      {(selectedUser.match_types || []).map((matchType) => (
                        <span key={matchType}>{MATCH_LABELS[matchType] || matchType}</span>
                      ))}
                    </div>
                    <h2 id="admin-refund-account-title">{selectedUser.display_name || '이름 없음'}</h2>
                    <p>{selectedUser.email || '이메일 없음'}</p>
                  </div>
                  <div className="admin-refund-account-meta">
                    <strong>{getSubscriptionLabel(selectedUser.subscription)}</strong>
                    <span>UID {selectedUser.uid}</span>
                  </div>
                </div>

                <div className="admin-refund-section-heading admin-refund-payment-heading">
                  <h2>결제 내역</h2>
                  <p>환불할 결제 한 건을 선택하세요.</p>
                </div>

                {selectedUser.payments.length === 0 ? (
                  <div className="admin-refund-empty-state admin-refund-empty-state-compact">
                    <h3>실제 결제 내역이 없어요</h3>
                    <p>쿠폰이나 추천 혜택만 받은 계정일 수 있어요.</p>
                  </div>
                ) : (
                  <div className="admin-refund-payment-list">
                    {selectedUser.payments.map((payment) => {
                      const normalizedStatus = String(payment.status || 'completed').toLowerCase();
                      return (
                        <article className="admin-refund-payment-card" key={payment.payment_key}>
                          <div className="admin-refund-payment-main">
                            <div className="admin-refund-payment-title-row">
                              <h3>{payment.plan_name}</h3>
                              <span className={`admin-refund-status admin-refund-status-${normalizedStatus}`}>
                                {payment.refund_reconciliation_required
                                  ? '기간 동기화 필요'
                                  : (STATUS_LABELS[normalizedStatus] || payment.status)}
                              </span>
                            </div>
                            <strong className="admin-refund-payment-amount">{formatCurrency(payment.amount)}</strong>
                            {payment.refund_type === 'prorated' && (
                              <p className="admin-refund-payment-refund-summary">
                                {`${formatCurrency(payment.refund_amount)} 일할 환불`}
                                {' · '}
                                {`${formatCurrency(payment.retained_amount)} 사용금액`}
                              </p>
                            )}
                            <dl className="admin-refund-payment-details">
                              <div>
                                <dt>결제일</dt>
                                <dd>{formatDate(payment.created_at, true)}</dd>
                              </div>
                              <div>
                                <dt>주문번호</dt>
                                <dd>{payment.order_no || '-'}</dd>
                              </div>
                              <div>
                                <dt>결제수단</dt>
                                <dd>{payment.payment_method || '-'}</dd>
                              </div>
                              <div>
                                <dt>제공 기간</dt>
                                <dd>{payment.granted_days ? `${payment.granted_days}일` : '확인 필요'}</dd>
                              </div>
                            </dl>
                          </div>
                          <div className="admin-refund-payment-action">
                            {payment.is_refundable_candidate ? (
                              <button
                                type="button"
                                onClick={() => openRefundModal(selectedUser, payment)}
                                aria-label={`${payment.plan_name} ${formatDate(payment.created_at)} 결제 환불하기`}
                              >
                                환불하기
                              </button>
                            ) : payment.refund_reconciliation_required ? (
                              <span className="admin-refund-action-warning">기간 동기화 필요</span>
                            ) : payment.refund_receipt_url ? (
                              <a href={payment.refund_receipt_url} target="_blank" rel="noreferrer">
                                환불 영수증
                              </a>
                            ) : (
                              <span>처리할 수 없음</span>
                            )}
                            {canResendPaymentDocuments(payment) && (
                              <button
                                className="admin-refund-docs-button"
                                disabled={Boolean(resendingDocumentsKey)}
                                onClick={() => resendDocuments(selectedUser, payment)}
                                type="button"
                              >
                                {resendingDocumentsKey === payment.payment_key ? '보내는 중' : '서류 다시 보내기'}
                              </button>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}

                {selectedUser.coupons.length > 0 && (
                  <section className="admin-refund-coupon-section" aria-labelledby="admin-refund-coupons-title">
                    <div className="admin-refund-section-heading">
                      <h2 id="admin-refund-coupons-title">쿠폰 혜택</h2>
                      <p>결제 환불 후에도 아래 쿠폰 기간은 보존돼요.</p>
                    </div>
                    <div className="admin-refund-coupon-list">
                      {selectedUser.coupons.map((coupon) => (
                        <div className="admin-refund-coupon-card" key={coupon.id}>
                          <strong>{coupon.coupon_code || '쿠폰 코드 없음'}</strong>
                          <span>{coupon.status || '-'} · {coupon.benefit_days ? `${coupon.benefit_days}일` : '기간 확인 필요'}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </section>
            )}
          </>
        )}
      </main>

      {modal && (
        <div className="admin-refund-modal-backdrop" onMouseDown={closeModal}>
          <section
            ref={modalRef}
            className="admin-refund-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-refund-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="admin-refund-modal-header">
              <div>
                <p>환불 전 확인</p>
                <h2 id="admin-refund-modal-title">{modal.payment.plan_name}</h2>
              </div>
              <button
                type="button"
                className="admin-refund-modal-close"
                onClick={closeModal}
                disabled={processing}
                aria-label="환불 창 닫기"
              >
                ×
              </button>
            </div>

            <fieldset
              className="admin-refund-mode-fieldset"
              disabled={processing}
            >
              <legend>환불 방식</legend>
              <div className="admin-refund-mode-options">
                <label className={refundMode === 'full' ? 'is-selected' : ''}>
                  <input
                    type="radio"
                    name="admin-refund-mode"
                    value="full"
                    checked={refundMode === 'full'}
                    onChange={() => handleRefundModeChange('full')}
                    autoFocus
                  />
                  <span>
                    <strong>전액 환불</strong>
                    <small>결제 잔액을 모두 환불해요</small>
                  </span>
                </label>
                <label className={refundMode === 'prorated' ? 'is-selected' : ''}>
                  <input
                    type="radio"
                    name="admin-refund-mode"
                    value="prorated"
                    checked={refundMode === 'prorated'}
                    onChange={() => handleRefundModeChange('prorated')}
                  />
                  <span>
                    <strong>일할 환불</strong>
                    <small>사용일을 제외한 금액만 환불해요</small>
                  </span>
                </label>
              </div>
            </fieldset>

            {refundMode === 'prorated' && (
              <div className="admin-refund-modal-field admin-refund-cutoff-field">
                <label htmlFor="admin-refund-cutoff-date">환불 기준일</label>
                <input
                  id="admin-refund-cutoff-date"
                  type="date"
                  value={prorationCutoffDate}
                  max={getTodayInKst()}
                  onChange={(event) => handleProrationCutoffChange(event.target.value)}
                  disabled={processing}
                />
                <p>결제일과 기준일을 모두 사용일에 포함해 한국시간으로 계산해요.</p>
              </div>
            )}

            {previewLoading && <RefundConsoleSkeleton />}

            {previewError && (
              <div className="admin-refund-inline-error" role="alert">
                <span>{previewError}</span>
                <button
                  type="button"
                  onClick={() => loadRefundPreview({
                    account: modal.account,
                    payment: modal.payment,
                    mode: refundMode,
                    cutoffDate: prorationCutoffDate,
                  })}
                >
                  다시 확인하기
                </button>
              </div>
            )}

            {preview && (
              <>
                {refundMode === 'prorated' && preview.proration ? (
                  <div className="admin-refund-proration-section">
                    <div className="admin-refund-proration-heading">
                      <div>
                        <span>서버 일할 계산</span>
                        <strong>
                          {preview.proration.payment_date}
                          {' ~ '}
                          {preview.proration.cutoff_date}
                        </strong>
                      </div>
                      <span>KST · 원 단위 반올림</span>
                    </div>
                    <div className="admin-refund-proration-grid">
                      <div>
                        <span>실결제 금액</span>
                        <strong>{formatCurrency(preview.proration.paid_amount)}</strong>
                      </div>
                      <div>
                        <span>사용 기간</span>
                        <strong>{preview.proration.used_days}일</strong>
                      </div>
                      <div>
                        <span>사용 금액</span>
                        <strong>{formatCurrency(preview.proration.retained_amount)}</strong>
                      </div>
                      <div>
                        <span>남은 기간</span>
                        <strong>{preview.proration.unused_days}일</strong>
                      </div>
                    </div>
                    <div className="admin-refund-proration-total">
                      <span>환불 금액</span>
                      <strong>{formatCurrency(preview.refundable_amount)}</strong>
                    </div>
                    <div className="admin-refund-proration-result">
                      <span>환불 후 PRO</span>
                      <strong>
                        {preview.impact?.subscription_remains_active
                          ? `${formatDate(preview.impact.next_end_date)}까지 유지`
                          : '이용 종료'}
                      </strong>
                    </div>
                  </div>
                ) : (
                  <div className="admin-refund-preview-summary">
                    <div>
                      <span>환불 금액</span>
                      <strong>{formatCurrency(preview.refundable_amount)}</strong>
                    </div>
                    <div>
                      <span>환불 후 PRO</span>
                      <strong>
                        {preview.impact?.subscription_remains_active
                          ? `${formatDate(preview.impact.next_end_date)}까지 유지`
                          : '이용 종료'}
                      </strong>
                    </div>
                  </div>
                )}
                <p className="admin-refund-irrevocable-note">
                  토스 결제 취소는 되돌릴 수 없어요. 서버가 계산한 금액과 이용 기간을 꼭 확인해주세요.
                </p>

                {preview.warnings?.length > 0 && (
                  <div className="admin-refund-warning-box">
                    {preview.warnings.map((warning) => <p key={warning}>{warning}</p>)}
                  </div>
                )}

                <div className="admin-refund-modal-field">
                  <label htmlFor="admin-refund-reason">환불 사유</label>
                  <textarea
                    id="admin-refund-reason"
                    value={refundReason}
                    onChange={(event) => setRefundReason(event.target.value)}
                    maxLength={200}
                    rows={3}
                    disabled={processing}
                  />
                </div>

                <label className="admin-refund-checkbox">
                  <input
                    type="checkbox"
                    checked={sendNotification}
                    onChange={(event) => setSendNotification(event.target.checked)}
                    disabled={processing}
                  />
                  <span>사용자에게 환불 안내 이메일 보내기</span>
                </label>

                {!preview.is_within_refund_window && (
                  <label className="admin-refund-checkbox admin-refund-checkbox-warning">
                    <input
                      type="checkbox"
                      checked={forceOutsideWindow}
                      onChange={(event) => setForceOutsideWindow(event.target.checked)}
                      disabled={processing}
                    />
                    <span>7일이 지난 결제를 관리자 권한으로 환불하기</span>
                  </label>
                )}

                <label className="admin-refund-checkbox admin-refund-checkbox-confirm">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                    disabled={processing}
                  />
                  <span>
                    {refundMode === 'prorated'
                      ? `${formatCurrency(preview.refundable_amount)} ${prorationCutoffDate} 기준 일할 환불 내용을 확인했어요`
                      : '환불 금액과 이용 기간 변경을 확인했어요'}
                  </span>
                </label>

                {processError && (
                  <div className="admin-refund-inline-error" role="alert">
                    <span>{processError}</span>
                  </div>
                )}

                <div className="admin-refund-modal-actions">
                  <button
                    type="button"
                    className="admin-refund-button-secondary"
                    onClick={closeModal}
                    disabled={processing}
                  >
                    돌아가기
                  </button>
                  <button
                    type="button"
                    className="admin-refund-button-danger"
                    onClick={processRefund}
                    disabled={
                      processing
                      || !preview.can_process
                      || !confirmed
                      || !refundReason.trim()
                      || (!preview.is_within_refund_window && !forceOutsideWindow)
                    }
                  >
                    {processing
                      ? '환불 처리 중'
                      : (
                        `${formatCurrency(preview.refundable_amount)} `
                        + `${refundMode === 'prorated' ? '일할 환불하기' : '환불하기'}`
                      )}
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default RefundConsolePage;
